import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  reportDataAF,
  ReportOwnerCategoryEnum,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { useReportId } from "./use-report-id";
import { useReportTitles } from "./use-report-titles";

/* this hook is intended to be used through useReportsContext for singleton instance */

export const useReportsCtxProvider = () => {
  const { reportId } = useReportId();
  const reportDto = useRecoilValue(reportDataAF(reportId));
  const report = useMemo(() => {
    return reportDto?.report;
  }, [reportDto?.report]);

  const reportTemplate = useMemo(() => {
    return reportDto?.reportTemplate;
  }, [reportDto?.reportTemplate]);

  const reportRenderType = useMemo(() => {
    return report?.renderType;
  }, [report]);

  const reportRows = useMemo(() => {
    return reportDto?.rows;
  }, [reportDto?.rows]);

  const reportLayout = useMemo(() => {
    return reportDto?.layout;
  }, [reportDto?.layout]);

  const { reportTitle, reportDescription } = useReportTitles(
    report,
    reportTemplate,
  );

  const { ownerCategory } = reportDto || {};

  const {
    hasPermissionCreateUserReports,
    hasPermissionCreateCompanyReports,
    hasPermissionCreateGlobalReports,
  } = useHaveCustomPermissions();

  const userHasPermission = useMemo(() => {
    const userReportsCondition =
      ownerCategory === ReportOwnerCategoryEnum.UserOnly &&
      hasPermissionCreateUserReports;
    const companyReportsCondition =
      ownerCategory === ReportOwnerCategoryEnum.AllUsers &&
      hasPermissionCreateCompanyReports;
    const globalReportsCondition = hasPermissionCreateGlobalReports;
    return (
      userReportsCondition || companyReportsCondition || globalReportsCondition
    );
  }, [
    hasPermissionCreateCompanyReports,
    hasPermissionCreateGlobalReports,
    hasPermissionCreateUserReports,
    ownerCategory,
  ]);

  const isReportDeleteAvailableForReport = useMemo(() => {
    if (report?.isUnDeletable || report?.isSystemProvided) {
      return false;
    }

    return userHasPermission;
  }, [report?.isSystemProvided, report?.isUnDeletable, userHasPermission]);

  const isReportEditAvailableForReport = useMemo(() => {
    if (report?.isSystemProvided) {
      return false;
    }

    return userHasPermission;
  }, [report?.isSystemProvided, userHasPermission]);

  return {
    reportDto,
    reportId,
    report,
    reportTemplate,
    reportRenderType,
    ownerCategory,
    reportTitle,
    reportDescription,
    reportRows,
    reportLayout,
    isReportDeleteAvailableForReport,
    isReportEditAvailableForReport,
  };
};
